import { FetchResult } from '@apollo/client'
import React, { useCallback, useMemo } from 'react'

import { BuyContext, BuyContextValue } from '@Components/BuyButton/context'
import { AddToCartFuncSync, useAddToCart } from '@Domains/Cart/Hooks/useAddToCart'
import { AddToCartMutation, AddToCartProductInput, LineItem } from '@Generated/graphql'

export const BuyRoot = ({
  children,
  milestoneID,
  onSuccess,
  preventDefault,
  productID,
  products,
  quantity = 1,
  sourceID,
  stopPropagation,
  useCache = true,
  variantID,
  withoutSuccessPrompt,
}: {
  children: React.ReactNode
  milestoneID?: string
  onSuccess?: (lineItem?: LineItem) => void
  preventDefault?: boolean
  productID: string
  products?: AddToCartProductInput[]
  quantity?: number
  sourceID?: string
  stopPropagation?: boolean
  useCache?: boolean
  variantID?: string
  withoutSuccessPrompt?: boolean
}) => {
  const [
    addToCart, {
      isDisabled,
      isLoading,
      prompts,
      reset,
      variables,
    },
  ] = useAddToCart({ sourceID, useCache, withoutSuccessPrompt })

  const triggerOnSuccess = useCallback((addToCartResult: FetchResult<AddToCartMutation>) => {
    if (onSuccess && addToCartResult?.data?.addToCart?.success) {
      onSuccess()
    }
    return addToCartResult
  }, [onSuccess])

  const handleAddToCart = useCallback((e?: React.MouseEvent) => {
    if (preventDefault) {
      e?.preventDefault()
    }
    if (stopPropagation) {
      e?.stopPropagation()
    }
    addToCart(products || [
      {
        quantity,
        productID,
        variantID,
        milestoneID,
      },
    ], {
      withoutSuccessPrompt,
    }).then(triggerOnSuccess)
  }, [addToCart, milestoneID, preventDefault, productID, products, quantity, stopPropagation, triggerOnSuccess, variantID, withoutSuccessPrompt])

  const handlePromptSubmit: AddToCartFuncSync = useCallback(
    (a, c) => addToCart(a, { ...c, withoutSuccessPrompt }).then(triggerOnSuccess),
    [addToCart, triggerOnSuccess, withoutSuccessPrompt],
  )

  const value: BuyContextValue = useMemo(() => ({
    handleAddToCart,
    isDisabled,
    isLoading,
    prompts,
    reset,
    variables,
    handlePromptSubmit,
    withoutSuccessPrompt,
  }), [handleAddToCart, isDisabled, isLoading, prompts, reset, variables, handlePromptSubmit, withoutSuccessPrompt])

  return (
    <BuyContext.Provider value={value}>
      {children}
    </BuyContext.Provider>
  )
}
